import React, { useCallback, useMemo } from 'react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { getStepRange } from '@atlaskit/editor-common/utils';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { Selection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useSubscribe } from '@atlaskit/rovo-triggers/main';
import { convertMarkdownToProsemirror } from '@atlassian/ai-model-io/convert-markdown-to-prosemirror';
import { useAgentsList } from '@atlassian/conversation-assistant-agent/controllers/agents';
import { mapToAssistanceServiceProduct } from '@atlassian/editor-ai-common/api/assistance-service';
import { getEditorAgent } from '@atlassian/generative-ai-modal/utils/agents';
import type { EditorAgent } from '@atlassian/generative-ai-modal/utils/agents';

import type { AIPlugin } from '../../../aiPluginType';
import type { EditorPluginAIProvider } from '../../../types/types';
import {
	insertAIContentAtCurrentPosition,
	replaceWithAIContent,
} from '../../prebuilt/config-items/utils/action-utils';

import { resolveInsertPositions } from './resolve-insert-positions';
import { useFavouriteRecommendedFetch } from './useFetchAgents';
import {
	createDispatchTransaction,
	hasEditorBeenFocused,
	isLastActiveEditorView,
	useTrackLastActiveEditorView,
} from './utils';

type SubscribeParams = Parameters<typeof useSubscribe>;

const topic: SubscribeParams[0] = {
	topic: 'ai-mate',
};

const getPmFragmentFromEditorSuggestionPayload = (
	payload: Extract<
		SubscribeParams[1] extends (payload: infer P) => void ? P : never,
		{ type: 'editor-suggestion' }
	>,
	schema: EditorView['state']['schema'],
	shouldUseRichContent: boolean,
) => {
	if (shouldUseRichContent && payload.data.richContent?.type === 'text/adf') {
		try {
			return Fragment.fromJSON(schema, payload.data.richContent.content);
		} catch {
			// Fall back to markdown if this editor schema cannot parse the rich ADF payload.
		}
	}

	return convertMarkdownToProsemirror({
		markdown: payload.data.content,
		schema,
		idMap: {},
	}).pmFragment;
};

const moveSelectionToEndOfChangedContent = (
	tr: ReturnType<typeof insertAIContentAtCurrentPosition> | ReturnType<typeof replaceWithAIContent>,
) => {
	const stepRange = getStepRange(tr);
	if (!stepRange) {
		return tr;
	}

	const selectionPos = Math.min(stepRange.to, tr.doc.content.size);
	try {
		tr.setSelection(Selection.near(tr.doc.resolve(selectionPos), 1));
	} catch {
		// Keep the selection chosen by the shared insert utility if this doc shape has no nearby cursor position.
	}

	return tr;
};

const SubscribeToRovoLegacy = ({
	editorView,
	product,
	onDocChangeByAgent,
	onAIProviderChanged,
	editorPluginAIProvider,
	positions,
	editorApi,
}: {
	editorApi: ExtractInjectionAPI<AIPlugin> | undefined;
	editorPluginAIProvider: EditorPluginAIProvider;
	editorView: EditorView;
	onAIProviderChanged: EditorPluginAIProvider['onAIProviderChanged'];
	onDocChangeByAgent: EditorPluginAIProvider['onDocChangeByAgent'];
	positions?: [number, number];
	product: EditorPluginAIProvider['product'];
}): React.JSX.Element => {
	const siteId = useMemo(
		() => editorPluginAIProvider?.getChannelVariables?.('rovo-agent')?.cloudId || '',
		[editorPluginAIProvider],
	);

	const agents = useAgentsList({
		assistanceServiceParams: {
			product: mapToAssistanceServiceProduct(product),
			experienceId: 'ai-mate',
			touchpointSource: 'editor-agent-subscribe-to-rovo',
			siteId,
		},
	});

	useTrackLastActiveEditorView(editorView);

	const callback = useCallback<SubscribeParams[1]>(
		(payload) => {
			if (payload.type === 'agent-changed' && onAIProviderChanged) {
				onAIProviderChanged(
					'rovo-chat',
					payload.data.agent
						? {
								type: 'rovo',
								agent: payload.data.agent,
							}
						: undefined,
				);
			}

			if (payload.type === 'editor-suggestion' && isLastActiveEditorView(editorView)) {
				const { state } = editorView;
				const insertedContentNodeTransformer =
					editorApi?.aiExperience?.actions?.getInsertedContentNodeTransformer?.();

				const isEditorFocused = hasEditorBeenFocused(editorView);
				const dispatchTransaction = createDispatchTransaction(
					editorView,
					editorApi,
					payload.data.mode === 'insert' && !isEditorFocused, // Scrolling to the end can only happen in insert mode
				);
				const shouldUseRichContent = fg('kd_lumina_insert_replace_response_as_adf');

				switch (payload.data.mode) {
					case 'insert': {
						const pmFragment = getPmFragmentFromEditorSuggestionPayload(
							payload,
							state.schema,
							shouldUseRichContent,
						);

						const insertPositions = resolveInsertPositions(
							positions,
							state.selection.from,
							state.selection.to,
							state.doc.content.size,
							isEditorFocused,
						);
						const transaction = insertAIContentAtCurrentPosition({
							editorView,
							positions: insertPositions,
							pmFragment,
							insertedContentNodeTransformer,
						});

						dispatchTransaction(
							shouldUseRichContent ? moveSelectionToEndOfChangedContent(transaction) : transaction,
						);
						break;
					}
					case 'replace': {
						const pmFragment = getPmFragmentFromEditorSuggestionPayload(
							payload,
							state.schema,
							shouldUseRichContent,
						);

						const transaction = replaceWithAIContent({
							editorView,
							positions: positions ?? [state.selection.from, state.selection.to],
							pmFragment,
							insertedContentNodeTransformer,
							options: { replaceDocOnEmptySelection: false },
						});

						dispatchTransaction(
							shouldUseRichContent ? moveSelectionToEndOfChangedContent(transaction) : transaction,
						);
						break;
					}
				}
				const matchedAgent = agents.find((agent) => agent.id === payload.data.agentId);

				if (matchedAgent && onDocChangeByAgent) {
					const agent: EditorAgent = getEditorAgent(matchedAgent);
					onDocChangeByAgent(agent);
				}
			}
		},
		[onAIProviderChanged, editorView, agents, onDocChangeByAgent, positions, editorApi],
	);
	useSubscribe(topic, callback);

	return <></>;
};

const SubscribeToRovoNew = ({
	editorView,
	onDocChangeByAgent,
	onAIProviderChanged,
	editorPluginAIProvider,
	positions,
	editorApi,
}: {
	editorApi: ExtractInjectionAPI<AIPlugin> | undefined;
	editorPluginAIProvider: EditorPluginAIProvider;
	editorView: EditorView;
	onAIProviderChanged: EditorPluginAIProvider['onAIProviderChanged'];
	onDocChangeByAgent: EditorPluginAIProvider['onDocChangeByAgent'];
	positions?: [number, number];
	product: EditorPluginAIProvider['product'];
}): React.JSX.Element => {
	const siteId = useMemo(
		() => editorPluginAIProvider?.getChannelVariables?.('rovo-agent')?.cloudId || '',
		[editorPluginAIProvider],
	);
	const agents = useFavouriteRecommendedFetch({ cloudId: siteId, editorState: editorView.state });

	useTrackLastActiveEditorView(editorView);

	const callback = useCallback<SubscribeParams[1]>(
		(payload) => {
			if (payload.type === 'agent-changed' && onAIProviderChanged) {
				onAIProviderChanged(
					'rovo-chat',
					payload.data.agent
						? {
								type: 'rovo',
								agent: payload.data.agent,
							}
						: undefined,
				);
			}

			if (payload.type === 'editor-suggestion' && isLastActiveEditorView(editorView)) {
				const { state } = editorView;
				const insertedContentNodeTransformer =
					editorApi?.aiExperience?.actions?.getInsertedContentNodeTransformer?.();

				const isEditorFocused = hasEditorBeenFocused(editorView);
				const dispatchTransaction = createDispatchTransaction(
					editorView,
					editorApi,
					payload.data.mode === 'insert' && !isEditorFocused, // Scrolling to the end can only happen in insert mode
				);
				const shouldUseRichContent = fg('kd_lumina_insert_replace_response_as_adf');

				switch (payload.data.mode) {
					case 'insert': {
						const pmFragment = getPmFragmentFromEditorSuggestionPayload(
							payload,
							state.schema,
							shouldUseRichContent,
						);

						const insertPositions = resolveInsertPositions(
							positions,
							state.selection.from,
							state.selection.to,
							state.doc.content.size,
							isEditorFocused,
						);
						const transaction = insertAIContentAtCurrentPosition({
							editorView,
							positions: insertPositions,
							pmFragment,
							insertedContentNodeTransformer,
						});

						dispatchTransaction(
							shouldUseRichContent ? moveSelectionToEndOfChangedContent(transaction) : transaction,
						);
						break;
					}
					case 'replace': {
						const pmFragment = getPmFragmentFromEditorSuggestionPayload(
							payload,
							state.schema,
							shouldUseRichContent,
						);

						const transaction = replaceWithAIContent({
							editorView,
							positions: positions ?? [state.selection.from, state.selection.to],
							pmFragment,
							insertedContentNodeTransformer,
							options: { replaceDocOnEmptySelection: false },
						});

						dispatchTransaction(
							shouldUseRichContent ? moveSelectionToEndOfChangedContent(transaction) : transaction,
						);
						break;
					}
				}

				const matchedAgent = agents.data.find((agent) => agent.id === payload.data.agentId);

				if (matchedAgent && onDocChangeByAgent) {
					onDocChangeByAgent(matchedAgent);
				}
			}
		},
		[onAIProviderChanged, editorView, agents, onDocChangeByAgent, positions, editorApi],
	);
	useSubscribe(topic, callback);

	return <></>;
};

/**
 * @private
 * @deprecated
 * No longer use! Deprecated API see https://hello.jira.atlassian.cloud/browse/COMMIT-20479
 */
export const SubscribeToRovo = ({
	editorView,
	product,
	onDocChangeByAgent,
	onAIProviderChanged,
	editorPluginAIProvider,
	positions,
	editorApi,
}: {
	editorApi: ExtractInjectionAPI<AIPlugin> | undefined;
	editorPluginAIProvider: EditorPluginAIProvider;
	editorView: EditorView;
	onAIProviderChanged: EditorPluginAIProvider['onAIProviderChanged'];
	onDocChangeByAgent: EditorPluginAIProvider['onDocChangeByAgent'];
	positions?: [number, number];
	product: EditorPluginAIProvider['product'];
}): React.JSX.Element => {
	return fg('platform_editor_ai_remove_agents_list') ? (
		<SubscribeToRovoNew
			editorPluginAIProvider={editorPluginAIProvider}
			editorView={editorView}
			onDocChangeByAgent={onDocChangeByAgent}
			product={product}
			onAIProviderChanged={onAIProviderChanged}
			positions={positions}
			editorApi={editorApi}
		/>
	) : (
		<SubscribeToRovoLegacy
			editorPluginAIProvider={editorPluginAIProvider}
			editorView={editorView}
			onDocChangeByAgent={onDocChangeByAgent}
			product={product}
			onAIProviderChanged={onAIProviderChanged}
			positions={positions}
			editorApi={editorApi}
		/>
	);
};
